import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { DateTime } from 'luxon';

import { formatDateWithOptions, formatWeekdayShort } from '@cf-shared/date-format';
import { BadgeColor } from 'components/cs-badge/cs-badge';
import { StatusChipTone } from 'components/cs-status-chip/cs-status-chip';
import { BOOT_LOCALE, LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import {
  breakableSetsText, OVERVIEW_BREAK_POINT, OverviewCell, OverviewCellKind, OverviewIntensityTag, OverviewMark, OverviewRow, OverviewSection,
  OverviewSessionTag, TRAINING_OVERVIEW_NO_VALUE, TRAINING_OVERVIEW_WINDOW_DAYS, TrainingOverviewModel,
} from 'services/training-overview';
import { Formatting } from 'util/formatting';

/** a tap on an exercise: the row, and the DOM event, so the host can open a menu at the tapped element */
export interface TrainingOverviewRowTap {
  row: OverviewRow;
  event: Event;
}

/**
 * The Training Overview (#5456, phase 3; redesigned in #6676): one card per workout of the program.
 * On wide screens a table with a row per exercise and a column for each of 4 sessions, paged with
 * "Show Earlier" and "Show Later"; on phones a table with a row per exercise and its newest session.
 *
 * Presentational only. It draws a model that `TrainingOverviewDataService` built and emits taps and
 * presses; it loads nothing and judges nothing. The marks and tags are decided in
 * `services/training-overview.ts`, the paging in `services/training-overview-paging.ts`. Every
 * element is an app component: `cs-card`, `cs-table`, `cs-badge` for tags, `cs-status-chip` for
 * marks. See design-docs/coaching-dashboard.md, "Phase 3: the Training Overview".
 */
@Component({
  selector: 'cs-training-overview',
  templateUrl: 'cs-training-overview.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTrainingOverviewComponent {
  protected LL = LL;
  protected OverviewCellKind = OverviewCellKind;
  protected OverviewMark = OverviewMark;
  protected BadgeColor = BadgeColor;
  protected readonly noValue = TRAINING_OVERVIEW_NO_VALUE;

  /** what to draw; undefined while the first load is running */
  @Input() model: TrainingOverviewModel | undefined;
  /** per section, whether "Show Earlier" can do anything; a missing entry counts as false */
  @Input() canShowEarlier: boolean[] = [];
  /** per section, whether "Show Later" can do anything; a missing entry counts as false */
  @Input() canShowLater: boolean[] = [];
  /** true while a load is running: a spinner before the first model, a fade over an old one */
  @Input() busy = false;
  /** true when the load failed; the grid is replaced by a message with a Retry button */
  @Input() failed = false;
  /** how many weeks of logs were loaded, for the "no logged workouts" sentence */
  @Input() loadedWeeks = TRAINING_OVERVIEW_WINDOW_DAYS / 7;

  @Output() retry = new EventEmitter<void>();
  @Output() rowTap = new EventEmitter<TrainingOverviewRowTap>();
  /** the index of the section whose "Show Earlier" was pressed */
  @Output() showEarlier = new EventEmitter<number>();
  /** the index of the section whose "Show Later" was pressed */
  @Output() showLater = new EventEmitter<number>();

  /** true when no section has a session at all */
  protected get hasNoSessions(): boolean {
    return !!this.model && this.model.sections.every(section => section.sessionCount === 0);
  }

  /** the wide table's columns, as board 1 of the approved mock-ups: Exercise, Sets, then one equal column per shown session */
  protected desktopColumns(section: OverviewSection): string {
    return `minmax(150px, 1.6fr) 64px repeat(${Math.max(1, section.columns.length)}, minmax(0, 1fr))`;
  }

  /**
   * The phone's note under a card's title: the day of the table's newest loaded session, written as
   * the column headings write it; not the shown desktop page's newest, which can be an older page
   */
  protected phoneNote(section: OverviewSection): TranslatedString {
    return LL.trainingOverview.phoneNote({ date: this.headerDate(section.newestDateIso ?? section.columns[section.columns.length - 1].dateIso) });
  }

  /** a session column's heading: "Wed 3/13", day and month in the language's own order, no year */
  protected headerDate(dateIso: string): string {
    const date = DateTime.fromISO(dateIso);
    return `${formatWeekdayShort(date, BOOT_LOCALE)} ${formatDateWithOptions(date, BOOT_LOCALE, { month: 'numeric', day: 'numeric' })}`;
  }

  /** "3 × 5" for a program row, "3" when it has no rep target, "–" when neither is known */
  protected setsTimesTarget(row: OverviewRow): string {
    if (!row.isInProgram || !row.setsText || row.setsText === TRAINING_OVERVIEW_NO_VALUE) return TRAINING_OVERVIEW_NO_VALUE;
    return row.repTargetText && row.repTargetText !== TRAINING_OVERVIEW_NO_VALUE
      ? `${row.setsText}${Formatting.times}${row.repTargetText}`
      : row.setsText;
  }

  /**
   * A phone row's reps line, "× 5, 5, 6", with break points after the times sign and each comma: a
   * long line then wraps there and only there, never inside a number (#6691).
   */
  protected phoneReps(cell: OverviewCell): string {
    return `${Formatting.times.trimStart()}${OVERVIEW_BREAK_POINT}${breakableSetsText(cell.repsText, Formatting.repSeparator, Formatting.times)}`;
  }

  /** a row can be tapped unless it is a grey row whose exercise no longer exists: nothing could open for it */
  protected isTappable(row: OverviewRow): boolean {
    return row.isInProgram || !row.isExerciseMissing;
  }

  protected onRowTap(row: OverviewRow, event: Event): void {
    if (!this.isTappable(row)) return;
    this.rowTap.emit({ row, event });
  }

  /** the mark chip's word, "Same" or "Down"; undefined for a cell with no mark */
  protected markText(cell: OverviewCell): TranslatedString | undefined {
    switch (cell.mark) {
      case OverviewMark.Same: return LL.trainingOverview.marks.same();
      case OverviewMark.Down: return LL.trainingOverview.marks.down();
      default: return undefined;
    }
  }

  /** the mark chip's tooltip: what the mark means; undefined for a cell with no mark */
  protected markInfo(cell: OverviewCell): TranslatedString | undefined {
    switch (cell.mark) {
      case OverviewMark.Same: return LL.trainingOverview.markInfo.same();
      case OverviewMark.Down: return LL.trainingOverview.markInfo.down();
      default: return undefined;
    }
  }

  /** "Same" in the warning colours, "Down" in the assertive ones, as the compact workout page draws them */
  protected markTone(cell: OverviewCell): StatusChipTone {
    return cell.mark === OverviewMark.Down ? StatusChipTone.Assertive : StatusChipTone.Warning;
  }

  /** the intensity badge's word, "Heavy", "Medium" or "Light"; undefined for a cell with no intensity */
  protected intensityText(cell: OverviewCell): TranslatedString | undefined {
    switch (cell.intensityTag) {
      case OverviewIntensityTag.Heavy: return LL.trainingOverview.tags.heavy();
      case OverviewIntensityTag.Medium: return LL.trainingOverview.tags.medium();
      case OverviewIntensityTag.Light: return LL.trainingOverview.tags.light();
      default: return undefined;
    }
  }

  /** the intensity badge's tooltip: which day it was; undefined for a cell with no intensity */
  protected intensityInfo(cell: OverviewCell): TranslatedString | undefined {
    switch (cell.intensityTag) {
      case OverviewIntensityTag.Heavy: return LL.trainingOverview.tagInfo.heavy();
      case OverviewIntensityTag.Medium: return LL.trainingOverview.tagInfo.medium();
      case OverviewIntensityTag.Light: return LL.trainingOverview.tagInfo.light();
      default: return undefined;
    }
  }

  /** the session tag badge's word, "Test", "Deload", "Break" or "Capped"; undefined for an untagged cell */
  protected sessionTagText(cell: OverviewCell): TranslatedString | undefined {
    switch (cell.sessionTag) {
      case OverviewSessionTag.Test: return LL.trainingOverview.tags.test();
      case OverviewSessionTag.Deload: return LL.trainingOverview.tags.deload();
      case OverviewSessionTag.Break: return LL.trainingOverview.tags.break();
      case OverviewSessionTag.Cap: return LL.trainingOverview.tags.cap();
      default: return undefined;
    }
  }

  /** the session tag badge's tooltip: why the session is lower on purpose; undefined for a cell with no tag */
  protected sessionTagInfo(cell: OverviewCell): TranslatedString | undefined {
    switch (cell.sessionTag) {
      case OverviewSessionTag.Test: return LL.trainingOverview.tagInfo.test();
      case OverviewSessionTag.Deload: return LL.trainingOverview.tagInfo.deload();
      case OverviewSessionTag.Break: return LL.trainingOverview.tagInfo.break();
      case OverviewSessionTag.Cap: return LL.trainingOverview.tagInfo.cap();
      default: return undefined;
    }
  }
}
